import React, { CSSProperties, useEffect, useState } from "react";
import {
  Avatar,
  Avatargroup,
  Box,
  Button,
  getColor,
  Icons,
  Modal,
  Table,
  Tooltip,
  Typography,
} from "fds";
import "./index.scss";
import { MeetingDetailDto, meetingDTO } from "../../types/meeting";
import { t, use } from "i18next";
import useMeetingStore from "../../redux/dispatcher/useMeetingStore";
import dayjs from "dayjs";
import utilsCommon from "../../shared/utils/common";
import AvatarGroup from "fds/src/components/atom/Avatargroup";
interface MeetingDetailModalProps {
  meetingInfo?: meetingDTO;
  open: boolean;
  onClose: () => void;
  classes?: string;
  style?: CSSProperties;
}
const MeetingDetailModal: React.FC<MeetingDetailModalProps> = ({
  meetingInfo,
  open,
  onClose,
  classes,
  style,
}) => {
  const { meetingState, getMeetingDetail } = useMeetingStore();
  const [meetingDetail, changeMeetingDetail] = useState<MeetingDetailDto>();
  useEffect(() => {
    getMeetingDetail({
      workgroupId: meetingInfo?.workgroupId ?? "",
      meetingId: meetingInfo?.meetingId ?? "",
      occurrenceId: meetingInfo?.occurrenceId,
    });
  }, [meetingInfo?.meetingId]);
  useEffect(() => {
    changeMeetingDetail(meetingState?.meetingDetail);
  }, [meetingState?.meetingDetail]);
  const meetingStatus =
    dayjs(meetingState?.meetingDetail?.meetingBasicInfo?.endTime).valueOf() >
      dayjs().valueOf() &&
    dayjs(meetingState?.meetingDetail?.meetingBasicInfo?.startTime).valueOf() <
      dayjs().valueOf()
      ? "live"
      : dayjs(
          meetingState?.meetingDetail?.meetingBasicInfo?.endTime
        ).valueOf() > dayjs().valueOf()
      ? "waiting"
      : "finished";
  const getOwnernameTag = () => {
    const meetingOwner = meetingDetail?.participantList?.filter(
      (user) => user.userId === meetingDetail?.meetingBasicInfo?.ownerId
    )[0];
    return meetingOwner ? (
      <Box alignItems="center" classes="mg mt-8">
        <Avatar
          uniqueCode={utilsCommon.getUserColor(
            meetingOwner?.userType,
            meetingOwner?.status ?? undefined,
            meetingOwner?.userName
          )}
          name={meetingOwner.userName ?? meetingOwner.userId}
          size={"32"}
          icon={meetingOwner.userName ? undefined : "user"}
        />
        <Tooltip
          message={meetingOwner.userName ?? meetingOwner.userId}
          classes="mg ml-8"
          maxWidth={"calc(100% - 45px)"}
        >
          <Typography ellipsis type="body2_rg">
            {meetingOwner.userName ?? meetingOwner.userId}
          </Typography>
        </Tooltip>
      </Box>
    ) : (
      <Box>?</Box>
    );
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("file.detail")}
      width={816}
      bodyHeight={556}
      classes="meetingDetailModal"
      body={
        <Box direction="column" width={"100%"}>
          <Box classes="meetingDetailModal-header">
            <Typography
              type="body4_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("common.common")}
            </Typography>
          </Box>
          <Box direction="column" width={"100%"}>
            <Box width={"100%"}>
              <Box direction="column" width={"calc(50% - 10px)"}>
                <Typography type="subtitle2">{t("meeting.topic")}</Typography>
                <Tooltip
                  message={meetingDetail?.zoomMeetingDetailDTO?.topic}
                  maxWidth={"100%"}
                >
                  <Typography type="body2_rg" classes="mg mt-8 ellipse-2line">
                    {meetingDetail?.zoomMeetingDetailDTO?.topic}
                  </Typography>
                </Tooltip>
              </Box>
              <Box
                direction="column"
                width={"calc(50% - 10px)"}
                classes="mg m-20"
              >
                <Typography type="subtitle2">
                  {t("meeting.meetingId")}
                </Typography>
                <Typography type="body2_rg" classes="mg mt-8">
                  {meetingDetail?.zoomMeetingDetailDTO?.id}
                </Typography>
              </Box>
            </Box>
            {meetingDetail?.zoomMeetingDetailDTO.agenda && (
              <Box direction="column" classes="mg mt-20">
                <Typography type="subtitle2">{t("meeting.content")}</Typography>
                <Tooltip message={meetingDetail?.zoomMeetingDetailDTO.agenda}>
                  <Typography type="body2_rg" classes="mg mt-8 ellipse-2line">
                    {meetingDetail?.zoomMeetingDetailDTO.agenda}
                  </Typography>
                </Tooltip>
              </Box>
            )}
            <Box width={"100%"} classes="mg mt-20">
              <Box direction="column" width={"calc(50% - 10px)"}>
                <Typography type="subtitle2">
                  {t("meeting.startTime")}
                </Typography>
                <Typography type="body2_rg" classes="mg mt-8 ellipse-2line">
                  {`${utilsCommon.renderDateFormat(
                    meetingDetail?.zoomMeetingDetailDTO.start_time
                  )} (GMT${utilsCommon.renderDateFormat(
                    meetingDetail?.zoomMeetingDetailDTO.start_time,
                    "Z"
                  )}) `}
                </Typography>
              </Box>
              <Box
                direction="column"
                width={"calc(50% - 10px)"}
                classes="mg m-20"
              >
                <Typography type="subtitle2">{t("meeting.status")}</Typography>
                <Typography type="body2_rg" classes="mg mt-8">
                  {meetingStatus === "waiting"
                    ? t("meeting.upcoming")
                    : meetingStatus === "live"
                    ? t("meeting.inProgress")
                    : t("meeting.finish")}
                </Typography>
              </Box>
            </Box>
            <Box width={"100%"} classes="mg mt-20">
              <Box direction="column" width={"calc(50% - 10px)"}>
                <Typography type="subtitle2">{t("file.lastUpdate")}</Typography>
                <Typography type="body2_rg" classes="mg mt-8">
                  {utilsCommon.renderDateFormat(
                    meetingDetail?.meetingBasicInfo?.updateTime
                  )}
                </Typography>
              </Box>
              <Box
                direction="column"
                width={"calc(50% - 10px)"}
                classes="mg m-20"
              >
                <Typography type="subtitle2">
                  {t("common.workgroup")}
                </Typography>
                <Tooltip
                  message={meetingDetail?.meetingBasicInfo?.workgroupName}
                  maxWidth={"100%"}
                >
                  <Typography type="body2_rg" classes="mg mt-8" ellipsis>
                    {meetingDetail?.meetingBasicInfo?.workgroupName}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          </Box>
          <Box classes="meetingDetailModal-header mg mt-20">
            <Typography
              type="body4_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("meeting.securtiy")}
            </Typography>
          </Box>
          <Box width={"100%"}>
            <Box direction="column" width={"calc(50% - 10px)"}>
              <Typography type="subtitle2"> {t("meeting.password")}</Typography>
              <Typography type="body2_rg" classes="mg mt-8">
                {meetingDetail?.zoomMeetingDetailDTO.password ? (
                  <Box>
                    <Icons
                      variant="line"
                      label="check"
                      stroke={getColor("COLOR_ICON_SUCCESS")}
                      classes={"mg mr-8"}
                    />
                    <Tooltip
                      message={meetingDetail?.zoomMeetingDetailDTO.password}
                    >
                      {Array(
                        meetingDetail?.zoomMeetingDetailDTO.password.length
                      )
                        .fill("*")
                        .map((item) => item)}
                    </Tooltip>
                  </Box>
                ) : (
                  <Typography
                    type="body2_rg"
                    exactColor={getColor("COLOR_TEXT_SECONDARY")}
                  >
                    {t("common.noUse")}
                  </Typography>
                )}
              </Typography>
            </Box>
            <Box
              direction="column"
              width={"calc(50% - 10px)"}
              classes="mg m-20"
            >
              <Typography type="subtitle2">
                {t("meeting.waitingRoom")}
              </Typography>
              {meetingDetail?.zoomMeetingDetailDTO.settings.waiting_room ? (
                <Icons
                  variant="line"
                  label="check"
                  stroke={getColor("COLOR_ICON_SUCCESS")}
                  classes={"mg mr-8"}
                />
              ) : (
                <Typography
                  type="body2_rg"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  {t("common.noUse")}
                </Typography>
              )}
            </Box>
          </Box>
          <Box classes="meetingDetailModal-header mg mt-20">
            <Typography
              type="body4_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("meeting.members")}
            </Typography>
          </Box>
          <Box width={"100%"}>
            <Box direction="column" width={"calc(50% - 10px)"}>
              <Typography type="subtitle2"> {t("meeting.host")}</Typography>
              {getOwnernameTag()}
            </Box>
            <Box
              direction="column"
              width={"calc(50% - 10px)"}
              classes="mg m-20"
            >
              <Typography type="subtitle2">
                {t("meeting.participants")}
              </Typography>
              <Tooltip
                message={meetingDetail?.participantList
                  ?.filter(
                    (user) =>
                      user.userId !== meetingDetail?.meetingBasicInfo?.ownerId
                  )
                  ?.map((item) => item?.userName || item?.userId)
                  .join(", ")}
                classes="mg mt-8"
              >
                <AvatarGroup
                  visibleLength={8}
                  size={"32"}
                  items={utilsCommon.sortMembersToAvatarGroup(
                    meetingDetail?.participantList?.filter(
                      (user) =>
                        user.userId !== meetingDetail?.meetingBasicInfo?.ownerId
                    )
                  )}
                />
              </Tooltip>
            </Box>
          </Box>
        </Box>
      }
      footer={
        <Box>
          <Button btnStyle="fill" label={t("common.close")} onClick={onClose} />
        </Box>
      }
    />
  );
};
export default MeetingDetailModal;
